import AssetLoader from "@/components/AssetLoader/AssetLoader";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import FormValidators, {
  FV,
} from "@/components/Form/Validation/FormValidators";
import Page from "@/components/PageView/Page";
import StructLoader from "@/components/StructLoader/StructLoader";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import BFFormSteps from "@/modules/abstract-ui/data/form-steps/BFFormSteps";
import BFAssetChooserSelect from "@/modules/abstract-ui/forms/chooser/BFAssetChooserSelect";
import BFChooserSelect from "@/modules/abstract-ui/forms/chooser/BFChooserSelect";
import BFDocumentEditor from "@/modules/abstract-ui/forms/document-editor/BFDocumentEditor";
import { getLetterFooter } from "@/modules/abstract-ui/forms/document-editor/BFLetterFooter";
import { getLetterHeader } from "@/modules/abstract-ui/forms/document-editor/BFLetterHeader";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFTemplateChooser from "@/modules/template-module/BFTemplateChooser";
import { TemplateAsset } from "@/modules/template-module/TemplateInterfaces";
import { saveTemplate } from "@/modules/template-module/TemplateMethods";
import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import { useConstants } from "@/redux/hooks";
import DataBusDefaults from "@/services/DataBusDefaults";
import { hasNoValue, hasValue } from "@/utils/Helpers";
import MQ from "@/utils/MatchQueryUtils";
import { useEffect, useState } from "react";
import { Field } from "react-final-form";
import { getTenantCommunicationVariables } from "../../APStartTenantCommunication.utils";
import "./APStartTenantCommunicationStep1TemplateType.scss";

export type APStartTenantCommunicationStep1Value = {
  type: string;
  template: {
    type: "new" | "template";
    id: string;
  };
  letterHeader: string;
  activityDisplayname: string;
  templateText: string;
};

interface APStartTenantCommunicationStep1TemplateTypeProps {
  initialValues?: APStartTenantCommunicationStep1Value;
  onReset: () => void;
  onSuccess: (values: APStartTenantCommunicationStep1Value) => void;
}

const getInitialValues = () => {
  return {
    template: null,
    type: null,
    letterHeader: null,
    activityDisplayname: "",
    templateText: "",
  } as APStartTenantCommunicationStep1Value;
};

export const LETTER_HEADER_TEMPLATE = "TENANT_COMMUNICATION_LETTER-HEADER";
const TEMPLATE_ID = "TENANT_COMMUNICATION-";
export const TEMPLATE_CHOOSER_ADD_NEW = "new";

const APStartTenantCommunicationStep1TemplateType = (
  props: APStartTenantCommunicationStep1TemplateTypeProps
) => {
  const [initial, setInitialState] = useState(
    hasValue(props.initialValues) ? props.initialValues : getInitialValues()
  );
  useEffect(() => {
    if (hasValue(props.initialValues)) {
      setInitialState(props.initialValues);
    } else {
      setInitialState(getInitialValues());
    }
  }, [props.initialValues]);
  const constants = useConstants();
  return (
    <StructLoader
      structTypes={["dunning", "unit", "orga", "objectKind"]}
      block
      unitTypes={constants.businessUnits}
      render={() => (
        <Page className={`ap-start-tenant-communication-step1`}>
          <div className={`__card`}>
            <FormStruct<any>
              onSubmit={async (
                values: APStartTenantCommunicationStep1Value
              ) => {
                //  const asset = (await SubmitService.submitDataAsync({
                //    type: 'asset',
                //    assetType: null, // AssetTypes.
                //    data: {
                //      _id: props.asset?._id,
                //      data: {
                //        ...transformFormdataToSubmitdata(values),
                //      },
                //    },
                //  })) as BaseAsset;

                props.onSuccess(values);
              }}
              additionalActions={
                <BFButton appearance="outline" onClick={() => props.onReset()}>
                  {i18n.t(
                    "apTemplate:StartTenantCommunication.CancelForm",
                    "Auswahl zurücksetzen"
                  )}
                </BFButton>
              }
              title={i18n.t(
                "apTemplate:StartTenantCommunication.Step1.Title",
                "Briefvorlage auswählen"
              )}
              subscription={{
                submitting: true,
              }}
              // notModal
              ignoreSubmitOnEnter
              usePrompt={"allowSubpaths"}
              submitText={i18n.t("Global.Buttons.continue", "Weiter")}
              initialValues={initial}
              render={(formProps) => (
                <>
                  <FormFieldValues
                    names={["type", "template", "activityDisplayname"]}
                  >
                    {([type, template, activityDisplayname]) => {
                      const step2Disabled = hasNoValue(type);
                      const step3Disabled =
                        step2Disabled || hasNoValue(template);
                      const step4Disabled =
                        step3Disabled || hasNoValue(activityDisplayname);

                      return (
                        <BFFormSteps
                          stepInfoWidth={200}
                          steps={[
                            {
                              title: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.SelectUnitTitle",
                                "Bereich auswählen"
                              ),
                              description: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.SelectUnitDescription",
                                "Wählen Sie den Bereich aus, für welchen Sie die Mieter anschreiben möchten."
                              ),
                              render: (disabled) => (
                                <div>
                                  <Field
                                    name="type"
                                    validate={FormValidators.compose(
                                      FormValidators.required()
                                    )}
                                  >
                                    {({ input: typeInput, meta }) => (
                                      <div className={`__flex __flex-column`}>
                                        <div className="__field __inline-flex">
                                          <BFChooserSelect
                                            {...typeInput}
                                            label={
                                              i18n.t(
                                                "apTemplate:StartTenantCommunication.Step1.type",
                                                "Bereich"
                                              ) + "*"
                                            }
                                            placeholder={i18n.t(
                                              "apTemplate:StartTenantCommunication.Step1.typePlaceholder",
                                              "Bereich auswählen"
                                            )}
                                            data={UnitStruct.getUnitSelectOptions()}
                                            {...FormValidators.getValidation(
                                              meta
                                            )}
                                            onChange={(value) => {
                                              typeInput.onChange(value);
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </Field>
                                </div>
                              ),
                            },
                            {
                              title: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.SelectTemplateTitle",
                                "Briefvorlage auswählen"
                              ),
                              description: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.SelectTemplateDescription",
                                "Wählen Sie eine Vorlage aus oder erstellen Sie eine neue, um die Mieter anzuschreiben."
                              ),
                              disabled: step2Disabled,
                              render: (disabled) => (
                                <div>
                                  <Field
                                    name="template"
                                    validate={FV.required()}
                                  >
                                    {({ input, meta }) => (
                                      <div className={`__field`}>
                                        <BFTemplateChooser
                                          addNewEntryText={i18n.t(
                                            "apTemplate:StartTenantCommunication.Step1.withoutTemplate",
                                            "Ohne Vorlage"
                                          )}
                                          disabled={disabled}
                                          identifier={TEMPLATE_ID}
                                          templateType={TEMPLATE_ID + type}
                                          value={input.value?.id}
                                          onSelect={(value: TemplateAsset) => {
                                            const { template, type, ...rest } =
                                              getInitialValues();

                                            let dataObj = {
                                              ...rest,
                                            };

                                            if (
                                              value._id ===
                                              TEMPLATE_CHOOSER_ADD_NEW
                                            ) {
                                              input.onChange({
                                                type: "new",
                                                id: TEMPLATE_CHOOSER_ADD_NEW,
                                              });
                                            } else {
                                              input.onChange({
                                                type: "template",
                                                id: value._id,
                                              });

                                              dataObj = value.data.data;
                                            }

                                            const setValue =
                                              formProps.form.mutators.setValue;

                                            Object.entries(dataObj).forEach(
                                              ([key, value]) => {
                                                setValue(key, value);
                                              }
                                            );
                                          }}
                                          height={260}
                                        />
                                      </div>
                                    )}
                                  </Field>
                                </div>
                              ),
                            },
                            {
                              title: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.EditTemplateTitle",
                                "Briefvorlage bearbeiten"
                              ),
                              description: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.EditTemplateDescription",
                                "Sie können die Briefvorlage anpassen, bevor Sie die Mieter anschreiben. Nutzen Sie die Platzhalter mit #, um die Vorlage zu personalisieren."
                              ),
                              disabled: step3Disabled,
                              render: (disabled) => (
                                <div>
                                  <div className={`__field`}>
                                    <Field
                                      name="letterHeader"
                                      validate={FV.required()}
                                    >
                                      {({ input, meta }) => (
                                        <BFAssetChooserSelect
                                          assetType={AssetTypes.Template}
                                          overlayHeight={300}
                                          matchQuery={MQ.and(
                                            MQ.eq("data.type", type),
                                            MQ.eq(
                                              "data.templateType",
                                              LETTER_HEADER_TEMPLATE
                                            )
                                          )}
                                          getOption={(node: TemplateAsset) => ({
                                            label: node.data.name,
                                            value: node._id,
                                          })}
                                          disabled={disabled}
                                          label={
                                            i18n.t(
                                              "apTemplate:StartTenantCommunication.Step1.letterHeader",
                                              "Briefkopf"
                                            ) + "*"
                                          }
                                          {...input}
                                          {...FV.getValidation(meta)}
                                        />
                                      )}
                                    </Field>
                                  </div>
                                  <div className={`__field`}>
                                    <Field
                                      name="activityDisplayname"
                                      validate={FV.required()}
                                    >
                                      {({ input, meta }) => (
                                        <BFInput
                                          disabled={disabled}
                                          label={i18n.t(
                                            "apTemplate:StartTenantCommunication.Step1.activityDisplayname",
                                            "Bezeichnung der Aktivität"
                                          )}
                                          placeholder={i18n.t(
                                            "apTemplate:StartTenantCommunication.Step1.activityDisplaynamePlaceholder",
                                            "Bezeichnung der Aktivität"
                                          )}
                                          {...input}
                                          {...FV.getValidation(meta)}
                                        />
                                      )}
                                    </Field>
                                  </div>
                                  <div>
                                    <FormFieldValues names={["letterHeader"]}>
                                      {([letterHead]) => {
                                        const renderTemplateText = (
                                          templateAsset: TemplateAsset
                                        ) => (
                                          <Field name="templateText">
                                            {({ input, meta }) => (
                                              <BFDocumentEditor
                                                allowPlaceholders
                                                allowPagebreak
                                                letter
                                                prefixHtml={
                                                  templateAsset
                                                    ? getLetterHeader(
                                                        templateAsset.data.data,
                                                        false
                                                      )
                                                    : null
                                                }
                                                suffixHtml={
                                                  templateAsset
                                                    ? getLetterFooter(
                                                        templateAsset.data.data
                                                      )
                                                    : null
                                                }
                                                disabled={disabled}
                                                variableSource={async (
                                                  search
                                                ) => {
                                                  return getTenantCommunicationVariables(
                                                    search
                                                  );
                                                }}
                                                label={i18n.t(
                                                  "apTemplate:StartTenantCommunication.Step1.templateLabel",
                                                  "Briefvorlage"
                                                )}
                                                {...input}
                                                {...FV.getValidation(meta)}
                                              />
                                            )}
                                          </Field>
                                        );
                                        return letterHead ? (
                                          <AssetLoader
                                            assetType={AssetTypes.Template}
                                            id={letterHead}
                                            render={(templateAsset) =>
                                              renderTemplateText(templateAsset)
                                            }
                                          />
                                        ) : (
                                          renderTemplateText(null)
                                        );
                                      }}
                                    </FormFieldValues>
                                  </div>
                                </div>
                              ),
                            },

                            {
                              preTitle: i18n.t(
                                "Global.Labels.optional",
                                "Optional"
                              ),
                              title: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.createTemplate",
                                "Vorlage erstellen"
                              ),
                              description: i18n.t(
                                "apTemplate:StartTenantCommunication.Step1.createTemplateInfo",
                                "Sie können optional die ausgewählten Daten als Vorlage erstellen"
                              ),
                              disabled: step4Disabled,
                              render: (disabled) => (
                                <div>
                                  <FormFieldValues
                                    names={[
                                      "letterHeader",
                                      "activityDisplayname",
                                      "templateText",
                                    ]}
                                  >
                                    {([
                                      letterHeader,
                                      activityDisplayname,
                                      templateText,
                                    ]) => (
                                      <BFButton
                                        disabled={disabled}
                                        appearance="outline"
                                        onClick={() => {
                                          saveTemplate(
                                            null,
                                            TEMPLATE_ID + type,
                                            {
                                              letterHeader,
                                              activityDisplayname,
                                              templateText,
                                            },
                                            undefined,
                                            () => {
                                              DataBusDefaults.reload({
                                                identifiers: [TEMPLATE_ID],
                                              });
                                            }
                                          );
                                        }}
                                      >
                                        {i18n.t(
                                          "Accounting.Export.createTemplate",
                                          "Vorlage erstellen"
                                        )}
                                      </BFButton>
                                    )}
                                  </FormFieldValues>
                                  {/* <div>
                                  {i18n.t(
                                    "Accounting.Export.template",
                                    "Vorlage"
                                  )}
                                </div> */}
                                </div>
                              ),
                            },
                          ]}
                        />
                      );
                    }}
                  </FormFieldValues>
                </>
              )}
            />
          </div>
        </Page>
      )}
    />
  );
};

export default APStartTenantCommunicationStep1TemplateType;
